import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 as BJSVector3 } from "@babylonjs/core/Maths/math.vector";
import { ERR_INVALID_ARGUMENT } from "../../../utils/error";
import { eq } from "../../../utils/math";
import { isNotNullOrEmpty } from "../../../utils/string";
import { deserializeQuaternion, deserializeVector3, serializeQuaternion, serializeVector3 } from "../../io/Serialization";
import { VECTOR3_ZERO } from "../../utils/MathUtils";
import { getRotationQuaternion } from "../../utils/MeshUtils";
const VECTOR3_ONE = new BJSVector3(1, 1, 1);
export function serializeNode(pModel, pContext, pNode, pProcessChilds = true, pNodeCallback) {
    if (pModel == null || pContext == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tNode = {};
    //position
    if (!pNode.position.equalsWithEpsilon(VECTOR3_ZERO))
        tNode.position = serializeVector3(pNode.position);
    //rotation
    const tRotation = getRotationQuaternion(pNode).clone();
    tRotation.normalize();
    if (!eq(tRotation.w, 1))
        tNode.rotation = serializeQuaternion(tRotation);
    //scale
    if (!pNode.scaling.equalsWithEpsilon(VECTOR3_ONE))
        tNode.scale = serializeVector3(pNode.scaling);
    //geometry 2d
    const tGeom2dId = pModel.geometries.getNodeGeometry2dId(pNode);
    if (tGeom2dId != null) {
        tNode.geometry2d = pContext.addGeometry2d(tGeom2dId);
    }
    if (pNode instanceof AbstractMesh) {
        //layer
        const tLayer = pModel.layers.getMeshLayer(pNode);
        if (isNotNullOrEmpty(tLayer))
            tNode.layer = pContext.addLayer(tLayer);
        //geometry and material
        const tGeomId = pModel.geometries.getMeshGeometryId(pNode);
        if (isNotNullOrEmpty(tGeomId)) {
            tNode.geometry = pContext.addGeometry(tGeomId);
            //material
            if (pNode.material != null && pModel.materials.hasMaterial(pNode.material.id))
                tNode.material = pContext.addMaterial(pNode.material.id);
        }
    }
    if (pNodeCallback != null)
        pNodeCallback(pNode, tNode);
    //serialize childs
    if (pProcessChilds) {
        const tChilds = pNode.getChildTransformNodes(true);
        if (tChilds.length > 0) {
            tNode.nodes = [];
            for (const tChild of tChilds) {
                const tChildNode = serializeNode(pModel, pContext, tChild, pProcessChilds, pNodeCallback);
                if (tChildNode != null) {
                    tNode.nodes.push(tChildNode);
                }
            }
        }
    }
    return (tNode);
}
export function deserializeNode(pModel, pContext, pIONode, pParentNode, pNodeCallback) {
    if (pModel == null || pContext == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    let tNode;
    let tMesh = null;
    //create a Mesh if it is a geometry node otherwise a TransformNode
    if (pIONode.geometry != null) {
        tMesh = new Mesh("", pModel.scene);
        tNode = tMesh;
    }
    else {
        tNode = new TransformNode("", pModel.scene);
    }
    //parent
    if (pParentNode != null)
        tNode.parent = pParentNode;
    //position
    if (pIONode.position != null)
        tNode.position = deserializeVector3(pIONode.position);
    //rotation
    if (pIONode.rotation != null)
        tNode.rotationQuaternion = deserializeQuaternion(pIONode.rotation);
    //scale
    if (pIONode.scale != null)
        tNode.scaling = deserializeVector3(pIONode.scale);
    //geometry 2d
    if (pIONode.geometry2d != null) {
        const tGeom2dId = pContext.getGeometry2dIdByIndex(pIONode.geometry2d);
        if (tGeom2dId != null) {
            pModel.geometries.setNodeGeometry2dId(tNode, tGeom2dId);
        }
    }
    if (tMesh != null) {
        //geometry
        if (pIONode.geometry != null) {
            const tGeomId = pContext.getGeometryIdByIndex(pIONode.geometry);
            if (tGeomId != null) {
                const tGeom = pModel.geometries.getGeometry(tGeomId);
                if (tGeom != null) {
                    tGeom.applyToMesh(tMesh);
                }
            }
        }
        //material
        if (pIONode.material != null) {
            const tMatId = pContext.getMaterialIdByIndex(pIONode.material);
            if (tMatId != null) {
                const tMat = pModel.materials.getMaterial(tMatId);
                if (tMat != null)
                    tMesh.material = tMat;
            }
        }
        //layer
        if (pIONode.layer != null) {
            const tName = pContext.getLayerNameByIndex(pIONode.layer);
            if (tName != null)
                pModel.layers.setMeshLayer(tMesh, tName);
        }
    }
    if (pNodeCallback != null)
        pNodeCallback(pIONode, tNode);
    //load children
    if (pIONode.nodes != null) {
        for (const tChild of pIONode.nodes) {
            deserializeNode(pModel, pContext, tChild, tNode, pNodeCallback);
        }
    }
    tNode.freezeWorldMatrix(); //freeze world matrix for better performance
    return (tNode);
}
